// src/components/PostPage.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./PostPage.css";
import { timeAgo } from "../utils/timeAgo";
import ReplyDialog from "./ReplyDialog.js";

function renderLinkedContent(text) {
  if (!text) return null;

  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = String(text).split(urlRegex);

  return parts.map((part, index) => {
    if (!/^https?:\/\//i.test(part)) {
      return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>;
    }

    const isYouTube = /youtube\.com|youtu\.be/i.test(part);

    if (isYouTube) {
      return <React.Fragment key={`youtube-${index}`}>{part}</React.Fragment>;
    }

    return (
      <a
        key={`link-${index}`}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
      >
        {part}
      </a>
    );
  });
}

const PostPage = ({ currentUser }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [showReplyDialog, setShowReplyDialog] = useState(false);

  const loggedInUser = (() => {
    if (currentUser) return currentUser;
    try {
      const raw = localStorage.getItem("currentUser");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();

  const fixURL = (url) => {
    if (!url) return null;
    return url.startsWith("http")
      ? url
      : `${process.env.REACT_APP_API_URL}${url}`;
  };

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/posts/${id}`);
        const data = await res.json();
        if (data && !data.error) setPost(data);
        else setPost(null);
      } catch (err) {
        console.error("❌ Error loading post:", err);
        setPost(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [id]);

  const fetchReplies = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/replies/${id}`
      );
      const data = await res.json();
      if (Array.isArray(data)) setReplies(data);
    } catch (err) {
      console.error("Error loading replies:", err);
    }
  };

  useEffect(() => {
    fetchReplies();
  }, [id]);

  const openReplyToReply = (reply) => {
    if (!loggedInUser?.username) {
      alert("Please log in to reply.");
      return;
    }

    setReplyingTo(reply);
    setShowReplyDialog(true);
  };

  const handleThreadReply = async (postId, replyData) => {
    if (!loggedInUser?.username) {
      throw new Error("Please log in to reply.");
    }

    const content =
      typeof replyData === "string" ? replyData : replyData?.content || "";
    const file =
      typeof replyData === "object" ? replyData?.file || null : null;
    const gif =
      typeof replyData === "object" ? replyData?.gif || null : null;
    const parentReplyId =
      typeof replyData === "object" ? replyData?.parent_reply_id || null : null;

    if (!content.trim() && !file && !gif) {
      throw new Error("Reply text, an image, or a GIF is required.");
    }

    const formData = new FormData();
    formData.append("content", content);

    if (file) formData.append("file", file);
    if (gif) formData.append("gif", gif);
    if (parentReplyId) {
      formData.append("parent_reply_id", String(parentReplyId));
    }

    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/posts/${postId}/reply`,
      {
        method: "POST",
        credentials: "include",
        body: formData,
      }
    );

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || "Failed to reply.");
    }

    await fetchReplies();
  };

  if (loading) return <p>Loading…</p>;
  if (!post) return <p>❌ Post not found or deleted.</p>;

  return (
    <div className="postpage-container">
      <div className="postpage-header">
        <h2 className="postpage-title">Post</h2>
        <button className="back-btn" onClick={() => navigate(-1)}>
          ← Back
        </button>
      </div>

      <div className="postpage-card">
        <div className="pp-header">
          <img
            src={fixURL(post.thumbnail)}
            alt="avatar"
            className="pp-avatar"
            onClick={() => navigate(`/profile/${post.username}`)}
          />
          <div>
            <strong>@{post.username}</strong>
            <div className="pp-date">{timeAgo(post.createdAt)}</div>
          </div>
        </div>

        <div className="pp-content">
          {renderLinkedContent(post.content)}
        </div>

        {post.media_url && (
          <div className="pp-media">
            {post.media_url.match(/\.(mp4|mov|webm)$/i) ? (
              <video controls src={fixURL(post.media_url)} />
            ) : (
              <img src={fixURL(post.media_url)} alt="media" />
            )}
          </div>
        )}

        {post.gif_url && (
          <div className="pp-media">
            <img src={post.gif_url} alt="gif" />
          </div>
        )}

        <div className="pp-actions">
          👍 {post.likes} &nbsp;&nbsp; 💬 {post.reply_count} &nbsp;&nbsp; 🔁{" "}
          {post.repost_count}
        </div>
      </div>

      <h3 className="pp-replies-title">Replies</h3>

      <div className="pp-replies">
        {replies.map((reply) => (
          <div className="pp-reply" key={reply.id}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "6px",
              }}
            >
              <img
                src={
                  reply.thumbnail
                    ? fixURL(reply.thumbnail)
                    : "/images/default-thumbnail.jpg"
                }
                alt={`@${reply.username}`}
                onClick={() => navigate(`/profile/${reply.username}`)}
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  objectFit: "cover",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              />

              <div>
                <strong
                  onClick={() => navigate(`/profile/${reply.username}`)}
                  style={{ cursor: "pointer" }}
                >
                  @{reply.username}
                </strong>

                <span
                  className="pp-reply-date"
                  style={{ marginLeft: "8px" }}
                >
                  {timeAgo(reply.createdAt)}
                </span>
              </div>
            </div>

            {reply.parent_reply_id && reply.parent_username && (
              <div
                style={{
                  fontSize: "0.85rem",
                  color: "#666",
                  marginBottom: "4px",
                }}
              >
                Replying to @{reply.parent_username}
              </div>
            )}

            {reply.content && <div>{reply.content}</div>}

            {reply.media_url && (
              <div className="pp-media" style={{ marginTop: "8px" }}>
                <img
                  src={fixURL(reply.media_url)}
                  alt="Reply attachment"
                />
              </div>
            )}

            {reply.gif_url && (
              <div className="pp-media" style={{ marginTop: "8px" }}>
                <img src={reply.gif_url} alt="Reply GIF" />
              </div>
            )}

            <div style={{ marginTop: "8px" }}>
              <button
                type="button"
                onClick={() => openReplyToReply(reply)}
                style={{
                  border: "none",
                  background: "transparent",
                  padding: 0,
                  cursor: "pointer",
                  fontSize: "0.9rem",
                }}
              >
                Reply
              </button>
            </div>
          </div>
        ))}
      </div>

      {showReplyDialog && replyingTo && (
        <ReplyDialog
          isOpen={showReplyDialog}
          onClose={() => {
            setShowReplyDialog(false);
            setReplyingTo(null);
          }}
          post={post}
          replyTo={replyingTo}
          onSubmit={handleThreadReply}
        />
      )}
    </div>
  );
};

export default PostPage;
